import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

export default function ChatBubble() {
  const { currentUser, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchContacts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/messages/contacts`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu am putut încărca contactele");
      setContacts(data?.data?.contacts || []);
    } catch (err) {
      console.error("Eroare contacts:", err);
      setError(err.message);
    }
  }, [authHeaders, token]);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu am putut încărca mesajele");
      setMessages(data?.data?.messages || []);
    } catch (err) {
      console.error("Eroare mesaje:", err);
      setError(err.message);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    if (!open || !currentUser || !token) return;
    setError("");
    setLoading(true);
    Promise.all([fetchContacts(), fetchMessages()])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, currentUser, token, fetchContacts, fetchMessages]);

  const selectedContact = useMemo(
    () => contacts.find((contact) => contact.id === recipientId) || null,
    [contacts, recipientId]
  );

  const conversation = useMemo(() => {
    if (!currentUser) return [];
    const selfEmail = currentUser.email;
    const recipientEmail = selectedContact?.email;
    if (!recipientEmail) return [];
    return (messages || []).filter(
      (msg) =>
        (msg.senderEmail === selfEmail && msg.recipientEmail === recipientEmail) ||
        (msg.senderEmail === recipientEmail && msg.recipientEmail === selfEmail)
    );
  }, [messages, currentUser, selectedContact]);

  const handleSend = async () => {
    if (!input.trim() || !recipientId || !token) return;
    const payload = { recipientId, text: input.trim() };
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu am putut trimite mesajul");
      if (data?.data?.message) {
        setMessages((prev) => [...prev, data.data.message]);
      }
      setInput("");
      setError("");
    } catch (err) {
      console.error("Eroare trimitere mesaj:", err);
      setError(err.message);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSend();
    }
  };

  if (!currentUser) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 bg-blue-600 text-white p-3 sm:p-4 rounded-full shadow-lg hover:bg-blue-700 z-50"
      >
        <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {open && (
        <div className="fixed bottom-20 right-4 sm:right-6 md:right-8 w-72 sm:w-80 h-[22rem] sm:h-96 bg-white shadow-xl rounded-xl flex flex-col z-[9999]">
          <div className="flex justify-between items-center bg-blue-600 text-white px-3 py-2 rounded-t-xl">
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className="text-black text-sm rounded px-1 py-0.5"
            >
              <option value="">Selectează destinatar</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name} ({contact.role})
                </option>
              ))}
            </select>
            <button onClick={() => setOpen(false)}>
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading && <p className="text-gray-400 text-sm text-center">Se încarcă...</p>}
            {!loading && conversation.length === 0 && (
              <p className="text-gray-400 text-sm text-center">Nicio conversație încă.</p>
            )}
            {!loading &&
              conversation.map((msg) => {
                const isMine = msg.senderEmail === currentUser.email;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs md:max-w-md p-2 rounded-2xl shadow text-sm ${
                        isMine
                          ? "bg-blue-600 text-white rounded-br-none"
                          : "bg-gray-200 text-gray-800 rounded-bl-none"
                      }`}
                    >
                      <p>{msg.text}</p>
                      <span className="text-[10px] opacity-70 block mt-1">
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            {error && (
              <p className="text-red-500 text-xs text-center" role="alert">
                {error}
              </p>
            )}
          </div>

          <div className="p-2 border-t flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scrie un mesaj..."
              className="flex-1 border rounded-lg px-2 py-1 text-sm"
              disabled={!recipientId}
            />
            <button
              onClick={handleSend}
              className="bg-blue-600 text-white px-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={!recipientId || !input.trim()}
            >
              Trimite
            </button>
          </div>
        </div>
      )}
    </>
  );
}
